import React, { useMemo, useState } from 'react';
import { ArrowRightIcon, MailIcon, PlayIcon, QuoteIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Waveform } from '../components/archive/AudioTile';
import { ResendLink } from '../components/auth/ResendLink';
import { PeopleTimeline } from '../components/landing/PeopleTimeline';
import { ARCHIVE_ITEMS, archiveStats } from '../data/archive';
import type { ArchiveItem } from '../data/archive';
import { MEMBERS, memberSpans } from '../data/community';
import type { RegistrationDraft } from '../types/registration';
interface JoinProps {
  onStart: () => void;
  resumeStep: number;
  totalSteps: number;
  completed: RegistrationDraft | null;
  onStartOver: () => void;
  onEnterPortal: () => void;
}

/** One measure for the whole page. Nothing is full-bleed. */
const PAGE = 'mx-auto w-full max-w-[64rem] px-4 sm:px-6';

/** Small caps label above a zone. Used sparingly. */
function Eyebrow({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="block font-mono text-eyebrow-caption uppercase tracking-[0.18em] text-ink-muted" data-id="element-999" data-mp="1b4fen5hpn028">
      {children}
    </span>;
}

/** Label left, plain description right. Used by both list zones. */
function Row({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <li className="grid gap-1 border-b border-rule py-4 sm:grid-cols-[14rem_minmax(0,1fr)] sm:gap-8" data-id="element-1000" data-mp="1ovar0rrf3zak">
      <span className="text-body font-semibold text-ink" data-id="element-1001" data-mp="1tux49178xfjd">{label}</span>
      <span className="text-body text-ink-muted" data-id="element-1002" data-mp="1yt743p3x14bb">{children}</span>
    </li>;
}

/** The questions a visitor actually arrives with, answered the way a person would. */
function buildQuestions(emptyYears: number) {
  return [{
    label: 'What is it?',
    body: 'A private photo album and member list for Stepping Stone alumni, staff and friends. Alumni volunteers run it. Staff check anything new before it goes up.'
  }, {
    label: 'What will I find?',
    body: 'Photos, old newsletters, recordings and stories, starting with the first meeting in Golden Hill in 1976. Plus everyone else who’s joined, sorted by when they were here.'
  }, {
    label: 'Why come back?',
    body: 'New things go up every week, someone writes a short letter at the start of each month, and get-togethers get posted here, like coffee for the ’90s crowd.'
  }, {
    label: 'Why add anything?',
    body: emptyYears > 0 ? `Because ${emptyYears} of the fifty years have nothing in them yet. If you were around then, you know more than we do.` : 'Because some years are nearly empty. If you were around then, you know more than we do.'
  }, {
    label: 'What it isn’t',
    body: 'No fundraising. No emails you didn’t sign up for. Nobody outside can see any of it.'
  }];
}
const PRIVACY = [{
  label: 'Your name',
  body: 'Other members see your first name and last initial. That’s it.'
}, {
  label: 'Your contact details',
  body: 'Nobody else sees your email or phone number.'
}, {
  label: 'Who can see you',
  body: 'Your call, and you make it first.'
}, {
  label: 'What you add',
  body: 'Someone on staff reads it before it goes up.'
}, {
  label: 'Search engines',
  body: 'Google can’t see any of this.'
}];

/** Prefer items inside the span, fall back to the rest of the archive. */
function within(items: ArchiveItem[], start: number, end: number, count: number): ArchiveItem[] {
  const inSpan = items.filter(item => item.year >= start && item.year <= end);
  const pool = inSpan.length >= count ? inSpan : [...inSpan, ...items.filter(item => !inSpan.includes(item))];
  return pool.slice(0, count).sort((a, b) => a.year - b.year);
}
function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}
export function Join({
  onStart,
  resumeStep,
  totalSteps,
  completed,
  onStartOver,
  onEnterPortal
}: JoinProps) {
  const [span, setSpan] = useState({
    start: 1994,
    end: 1999
  });
  const inProgress = resumeStep > 0 && !completed;
  const stats = archiveStats();
  const questions = buildQuestions(Math.max(stats.yearsTotal - stats.yearsCovered, 0));
  const photographs = useMemo(() => ARCHIVE_ITEMS.filter(item => item.kind === 'photograph' && item.image), []);
  const hero = photographs[0];
  const plate = useMemo(() => within(photographs, span.start, span.end, 4), [photographs, span]);
  const recording = within(ARCHIVE_ITEMS.filter(item => item.kind === 'audio' && item.transcript), span.start, span.end, 1)[0];
  const memory = within(ARCHIVE_ITEMS.filter(item => item.kind === 'memory' && item.quote), span.start, span.end, 1)[0];
  const artifact = within(ARCHIVE_ITEMS.filter(item => item.kind === 'artifact' && item.image), span.start, span.end, 1)[0];

  // Same rule as the year slider in the hero, so the two numbers always agree.
  const overlapping = useMemo(() => MEMBERS.filter(member => memberSpans(member).some(s => s.start <= span.end && s.end >= span.start)).length, [span]);
  const unknownVoices = recording?.speaker?.toLowerCase().includes('unidentified');
  return <div className="flex flex-col bg-card" data-id="element-1003" data-mp="36crusc948swz">
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className={`${PAGE} py-12 lg:py-16`} data-id="element-1004" data-mp="3b9s5x1ktoluy">
        <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-12" data-id="element-1005" data-mp="2el2lb3ht9f5a">
          <div className="flex flex-col gap-5" data-id="element-1006" data-mp="2ilw5bls4u9sw">
            <Eyebrow data-id="element-1007" data-mp="2imnwmkep5ldd">1976–2026 · Members only</Eyebrow>
            <h1 className="max-w-[19ch] text-display font-semibold leading-tight text-ink" data-id="element-1008" data-mp="2nl8sk7mx1iw0">
              Fifty years of Stepping Stone. Find the people from yours.
            </h1>
            <p className="max-w-[52ch] text-body text-ink" data-id="element-1009" data-mp="2shpkze15ejew">
              A private site for anyone who’s been part of Stepping Stone since 1976. Old photos,
              recordings and stories, plus everyone else who’s signed up, all sorted by year.
            </p>
            <p className="max-w-[46ch] text-small text-ink-muted" data-id="element-1010" data-mp="3e9ihmt9hi56k">
              If you went through residential, Stepping Out or sober living, worked here,
              volunteered or sat on the board, you’re in. Same if you’re family or a friend of
              someone who did.
            </p>

            {completed ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-1011" data-mp="3j8by8uk2icay">
                <span className="flex items-center gap-2 text-heading font-semibold text-ink" data-id="element-1012" data-mp="2qlius4famyx9">
                  <MailIcon aria-hidden="true" className="h-4 w-4 text-primary-text" data-id="element-1013" data-mp="35aeyxqe8brsl" />
                  Check your email
                </span>
                <p className="text-small text-ink-muted" data-id="element-1014" data-mp="30ine7whr8gad">
                  We sent a link to{' '}
                  <span className="text-ink" data-id="element-1015" data-mp="2cst59q0k3bve">{completed.email.trim() || 'your inbox'}</span> to set
                  your password.
                </p>
                <div className="flex flex-wrap items-start gap-3 pt-1" data-id="element-1016" data-mp="ncywk1cwxmmg">
                  <Button onClick={onEnterPortal} data-id="element-1017" data-mp="nbnuq7312oxz">Go to the portal</Button>
                  <ResendLink email={completed.email.trim() || 'your inbox'} data-id="element-1018" data-mp="s4gzguyhxxa2" />
                  <Button variant="quiet" onClick={onStartOver} data-id="element-1019" data-mp="dfkho66n15w9">
                    Walk through it again
                  </Button>
                </div>
              </div> : <div className="flex flex-wrap items-center gap-3" data-id="element-1020" data-mp="162yk2jt3utnr">
                <Button size="lg" onClick={onStart} data-id="element-1021" data-mp="161nf4gnxbfv2">
                  {inProgress ? 'Pick up where you left off' : 'Join, it’s free'}
                </Button>
                {inProgress ? <Button variant="quiet" onClick={onStartOver} data-id="element-1022" data-mp="x4uzbppy20x0">
                    Start over
                  </Button> : null}
              </div>}
          </div>

          <PeopleTimeline start={span.start} end={span.end} onChange={setSpan} data-id="element-1023" data-mp="3hv3ujf0ng31m" />
        </div>
      </section>

      {/* ── WHAT IT IS ───────────────────────────────────────────────── */}
      <section aria-labelledby="what" className={`${PAGE} border-t border-rule py-12`} data-id="element-1024" data-mp="he2xudr1ufuz">
        <h2 id="what" className="text-section-heading font-semibold text-ink" data-id="element-1025" data-mp="cc58d1s22otl">
          The short version
        </h2>

        <ul className="mt-6 border-t border-rule" data-id="element-1026" data-mp="8e7irxudi0po">
          {questions.map(item => <Row key={item.label} label={item.label} data-id="element-1027" data-mp="3euur9rrmnli">
              {item.body}
            </Row>)}
        </ul>

        <p className="mt-6 text-small text-ink-muted" data-id="element-1028" data-mp="w1o0k6k0id2d">
          So far: {plural(stats.total, 'thing', 'things')} in the archive, including{' '}
          {plural(stats.byKind.photograph, 'photo', 'photos')},{' '}
          {plural(stats.byKind.audio, 'recording', 'recordings')} and{' '}
          {plural(stats.byKind.memory, 'story', 'stories')}. More every week.
        </p>
      </section>

      {/* ── FROM THE ARCHIVE ─────────────────────────────────────────── */}
      <section aria-labelledby="taste" className={`${PAGE} border-t border-rule py-12`} data-id="element-1029" data-mp="1ez8rdj6c7lpm">
        <h2 id="taste" className="sr-only" data-id="element-1030" data-mp="1md9aqi3ix4zg">
          From the archive
        </h2>
        <Eyebrow data-id="element-1031" data-mp="1rbkk6rpwpdh5">From the archive</Eyebrow>

        {hero?.image ? <figure className="mt-6 flex flex-col gap-2" data-id="element-1032" data-mp="1w9co6k3tsq1p">
            <img src={hero.image.src} alt={hero.image.alt} className="aspect-[16/7] w-full rounded-card object-cover" data-id="element-1033" data-mp="1wbyu6fb2ixht" />
            <figcaption className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-1034" data-mp="259rb1nwfud7b">
              {hero.year} · {hero.title}
            </figcaption>
          </figure> : null}

        <ul className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-4" data-id="element-1035" data-mp="2f0vm4ciztdwv">
          {plate.map(photo => <li key={photo.id} className="flex flex-col gap-2" data-id="element-1036" data-mp="2jynef7njv21l">
              <img src={photo.image!.src} alt={photo.image!.alt} className="aspect-[4/3] w-full rounded-field object-cover" data-id="element-1037" data-mp="2o23ao2vpj0dr" />
              <span className="font-mono text-eyebrow-caption tracking-[0.14em] text-ink-muted" data-id="element-1038" data-mp="1dxk829ym09cz">
                {photo.year}
              </span>
            </li>)}
        </ul>

        <div className="mt-10 grid gap-4 border-t border-rule pt-10 lg:grid-cols-3" data-id="element-1039" data-mp="1nv8cjrm0ov8c">
          {recording ? <div className="flex flex-col gap-4 rounded-card bg-ink p-5" data-id="element-1040" data-mp="1slx6ddl664an">
              <span className="flex items-center justify-between font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-on-ink opacity-70" data-id="element-1041" data-mp="1wnab1kcj01fz">
                <span data-id="element-1042" data-mp="21kscyqs4lgeh">{recording.year} · Recording</span>
                <span data-id="element-1043" data-mp="26lpwp7ssaii3">{recording.duration}</span>
              </span>
              <span className="flex items-center gap-3 text-highlight" data-id="element-1044" data-mp="26jdhh2eeum8q">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-highlight" data-id="element-1045" data-mp="2bk147bvlwgx5">
                  <PlayIcon aria-hidden="true" className="h-5 w-5 translate-x-0.5 text-on-highlight" data-id="element-1046" data-mp="c99po4s5z0c6" />
                </span>
                <Waveform seed={recording.id} className="min-w-0 flex-1" data-id="element-1047" data-mp="2uico19yh1if0" />
              </span>
              <p className="text-body italic text-on-ink" data-id="element-1048" data-mp="2pp9u30ps86ya">{recording.transcript}</p>
              <p className="text-small text-on-ink opacity-60" data-id="element-1049" data-mp="2lpj7783dmaz1">
                {unknownVoices ? 'We don’t know who’s talking. Maybe you do. Members can hear the whole thing.' : `${recording.speaker}. Members can hear the whole thing.`}
              </p>
            </div> : null}

          {memory ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-1050" data-mp="39c7ts73byrlz">
              <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-1051" data-mp="2zfv0vbfm0hsa">
                {memory.year} · Story
              </span>
              <QuoteIcon aria-hidden="true" className="h-5 w-5 text-ink-subtle" data-id="element-1052" data-mp="1t1dhe6xgtvsc" />
              <p className="text-body italic text-ink" data-id="element-1053" data-mp="1nzxzuouhy7ep">“{memory.quote}”</p>
              <p className="text-small text-ink-muted" data-id="element-1054" data-mp="1phd0zgc6dqxs">Written by a member. Members can see who.</p>
            </div> : null}

          {artifact?.image ? <div className="flex flex-col gap-3 rounded-card border border-rule bg-card p-5 shadow-card" data-id="element-1055" data-mp="1uell3mgvw51o">
              <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] text-ink-muted" data-id="element-1056" data-mp="1lg9nv1lii909">
                {artifact.year} · From the files
              </span>
              <img src={artifact.image.src} alt={artifact.image.alt} className="h-40 w-full rounded-field bg-canvas object-contain p-2" data-id="element-1057" data-mp="16pakq63g4co2" />
              <p className="text-body font-medium text-ink" data-id="element-1058" data-mp="xom6baa54aan">{artifact.title}</p>
              <p className="text-small text-ink-muted" data-id="element-1059" data-mp="11stth30y9i6r">{artifact.note}</p>
            </div> : null}
        </div>
      </section>

      {/* ── FIND YOUR YEARS ──────────────────────────────────────────── */}
      <section aria-labelledby="years" className={`${PAGE} border-t border-rule py-12`} data-id="element-1060" data-mp="stoww28y7yx8">
        <h2 id="years" className="text-section-heading font-semibold text-ink" data-id="element-1061" data-mp="962ibwozh7tk">
          People come back
        </h2>
        <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-1062" data-mp="493shqccrn16">
          Maybe you did residential in the ’90s, came back to volunteer in 2012, and still show up
          to the gala. Add every stretch, so people from each one can find you.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-4" data-id="element-1063" data-mp="s3dh7g6pdz8h">
          <p aria-live="polite" className="text-body text-ink" data-id="element-1064" data-mp="n64xhb5wpn54">
            {overlapping > 0 ? <>
                <span className="font-semibold" data-id="element-1065" data-mp="11tg7v9qcr13n">
                  {plural(overlapping, 'person', 'people')} here
                </span>{' '}
                overlap with {span.start}–{span.end}.
              </> : <>Nobody from {span.start}–{span.end} has joined yet.</>}
          </p>
          <Button variant="secondary" onClick={onStart} data-id="element-1066" data-mp="16vfdkq9kea3r">
            {overlapping > 0 ? 'See who' : 'Be the first'}
          </Button>
        </div>
      </section>

      {/* ── PRIVACY ──────────────────────────────────────────────────── */}
      <section id="privacy" aria-labelledby="privacy-title" className={`${PAGE} border-t border-rule py-12`} data-id="element-1067" data-mp="3a2czuvtoefge">
        <h2 id="privacy-title" className="text-section-heading font-semibold text-ink" data-id="element-1068" data-mp="3ezwevxy76w9e">
          Who sees what
        </h2>
        <p className="mt-2 max-w-[52ch] text-body text-ink-muted" data-id="element-1069" data-mp="2hfjw04kzp026">
          You set your privacy before we ask anything personal, and you can change it whenever you
          want.
        </p>

        <ul className="mt-6 border-t border-rule" data-id="element-1070" data-mp="2rabvjdy4twzl">
          {PRIVACY.map(item => <Row key={item.label} label={item.label} data-id="element-1071" data-mp="2veruev9bw45b">
              {item.body}
            </Row>)}
        </ul>
      </section>

      {/* ── JOIN ─────────────────────────────────────────────────────── */}
      <section className={`${PAGE} border-t border-rule py-12`} data-id="element-1072" data-mp="ap2hwow3kufs">
        <h2 className="text-section-heading font-semibold text-ink" data-id="element-1073" data-mp="1v3qlthct35yu">Come on in</h2>
        <Button size="lg" className="mt-5" onClick={onStart} data-id="element-1074" data-mp="1r36pgg0246dr">
          {inProgress ? 'Pick up where you left off' : 'Join, it’s free'}
          <span className="font-mono text-eyebrow-caption uppercase tracking-[0.14em] opacity-70" data-id="element-1075" data-mp="1mavqpgh1yqms">
            3 min
          </span>
          <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-1076" data-mp="1ceiti8matuna" />
        </Button>
        <p className="mt-3 text-small text-ink-muted" data-id="element-1077" data-mp="1cc66hh1oou3d">
          Seven questions. Stop whenever you want and pick up where you left off.
        </p>
      </section>
    </div>;
}